import { StatusId, OperationBCI, CommandeRang, OperationFormulaire } from '../types';
import { Article } from '../../../services/articlesService';

// Derivations PURES de l'onglet Consultation acheteur-BC : formatage des articles,
// lecture seule, statut de consultation, validation, corps de sauvegarde. Aucune
// dependance React. Gating par id de statut.

const parseRang = (value: string | number): number => parseInt(String(value), 10);

const getCmd = (operation: OperationBCI, rang: number): CommandeRang | undefined =>
  (operation.commandes || []).find((c) => parseRang(c.rang) === rang);

const isBrouillon = (operation: OperationBCI): boolean =>
  operation.status?.id === StatusId.BrouillonAcheteur;

// Mapping champ API (snake_case) -> champ formulaire (camelCase) pour remapper les
// erreurs 422. Source unique (cf. ancienne API_TO_FORM_FIELD_BC). Swap volontaire :
//   facturation -> coordonneesMaitriseDOuvrage ; coordonnees_maitrise_ouvrage -> coordonneesMaitrise.
export const API_TO_FORM_FIELD_BC: Record<string, string> = {
  nom: 'nomOperation',
  reference: 'referenceInterne',
  besoin: 'besoin',
  adresse: 'adresse',
  code_postal: 'codePostal',
  ville: 'ville',
  facturation: 'coordonneesMaitriseDOuvrage',
  coordonnees_maitrise_ouvrage: 'coordonneesMaitrise',
  condition_visite: 'conditionVisite',
};

export const mapApiErrorsToForm = (errors: Record<string, string[]>): Record<string, string> => {
  const mapped: Record<string, string> = {};
  Object.keys(errors).forEach((field) => {
    const messages = errors[field];
    if (Array.isArray(messages) && messages.length > 0) {
      mapped[API_TO_FORM_FIELD_BC[field] || field] = messages[0];
    }
  });
  return mapped;
};

// --- Articles existants (tableau financier acheteur, mode brouillon ou lecture) -
export const formatExistingArticles = (operation: OperationBCI, rang: number): Article[] => {
  const cmd = getCmd(operation, rang);
  const articles = cmd?.articles || [];

  return articles.map((item): Article => {
    const catalogue = item.catalogue;
    const articleData = catalogue?.article;

    const departementId = operation.lot_departement?.departement?.id || '00';
    const lotTechniqueId = operation.lot_technique?.id || '000';
    const code = catalogue?.code || '';
    const reference = `${departementId.toString().padStart(2, '0')} ${lotTechniqueId} ${code} ${rang}`;

    const prixUnitaire = parseFloat(String(articleData?.prix_unitaire ?? '')) || 0;
    const quantite = parseInt(String(articleData?.quantity ?? ''), 10) || 1;
    const seuilA = catalogue?.seuil_a || 5;
    const seuilB = catalogue?.seuil_b || 10;

    return {
      id: articleData?.id?.toString() || '',
      catalogueId: catalogue?.id?.toString() || '',
      code: reference,
      nom: catalogue?.nom || 'Article sans nom',
      description: catalogue?.description || '',
      prix: prixUnitaire,
      unite: catalogue?.unite || 'Unité',
      quantite,
      totalHT: prixUnitaire * quantite,
      prixDegressifs: {
        palier1: { min: 1, max: seuilA, prix: prixUnitaire },
        palier2: { min: seuilA + 1, max: seuilB, prix: prixUnitaire },
        palier3: { min: seuilB + 1, prix: prixUnitaire },
      },
    };
  });
};

export const totalArticlesHT = (articles: Article[]): number =>
  articles.reduce((sum, a) => sum + a.totalHT, 0);

// --- Lecture seule ------------------------------------------------------------
// Editable uniquement en brouillon acheteur et non expire (acheteur-only).
export const isConsultationSectionReadOnly = (operation: OperationBCI, expired: boolean): boolean =>
  expired || !isBrouillon(operation);

// --- Statut de consultation (encart "lancee / transferee") --------------------
export interface ConsultationStatusView {
  type: 'transferee' | 'none';
  isVisible: boolean;
  showOffreEnvoyee: boolean;
}

const HIDDEN: ConsultationStatusView = { type: 'none', isVisible: false, showOffreEnvoyee: false };

export const computeConsultationStatus = (operation: OperationBCI): ConsultationStatusView => {
  const commandes = operation.commandes || [];
  const sid = operation.status?.id;

  if (operation.transferred === true) {
    // Apres refus du rang 1 en phase d'analyse : ne rien afficher (rang suivant pas lance).
    if (sid === StatusId.AAnalyser) {
      const rang1 = commandes.find((c) => parseRang(c.rang) === 1);
      if (rang1 && rang1.id_status === StatusId.Refuse) {
        return HIDDEN;
      }
    }

    // Un rang est effectivement en cours cote prestataire : AChiffrer(11) ou AttenteAnalyse(13).
    const rangEnCours = commandes.find(
      (c) => c.id_status === StatusId.AChiffrer || c.id_status === StatusId.AttenteAnalyse,
    );
    if (rangEnCours) {
      return {
        type: 'transferee',
        isVisible: true,
        // "Offre envoyee par le prestataire" : statut PropositionFaite(15).
        // (statut ambigu cote DB -> a confirmer.)
        showOffreEnvoyee: sid === StatusId.PropositionFaite,
      };
    }
    return HIDDEN;
  }

  return HIDDEN;
};

// --- Validation du formulaire -------------------------------------------------
export interface ConsultationValidation {
  valid: boolean;
  errors: Record<string, string>;
}

export const validateConsultationForm = (formulaire?: OperationFormulaire): ConsultationValidation => {
  const f = formulaire || {};
  const errors: Record<string, string> = {};

  if (!f.nomOperation?.trim()) errors.nomOperation = "Le nom de l'opération est obligatoire";
  if (!f.adresse?.trim()) errors.adresse = "L'adresse est obligatoire";
  if (!f.codePostal?.trim()) errors.codePostal = 'Le code postal est obligatoire';
  if (!f.ville?.trim()) errors.ville = 'La ville est obligatoire';
  if (!f.besoin?.trim()) errors.besoin = 'Le cahier des charges est obligatoire';
  if (f.visitePreavis && !f.conditionVisite?.trim()) {
    errors.conditionVisite = 'Les conditions de visite sont obligatoires';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

// --- Corps de sauvegarde (PUT /bdc/operation/{id}) ----------------------------
const formatDateForApi = (dateStr?: string | null): string | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
};

const addDays = (base: Date, days: number): Date => {
  const d = new Date(base);
  d.setDate(base.getDate() + days);
  return d;
};

// Reconstruit le payload de sauvegarde avec les memes fallbacks de dates que l'ancienne
// feuille. Les libelles de coordonnees suivent le swap volontaire cote API BC.
export const buildConsultationSaveBody = (operation: OperationBCI): Record<string, unknown> => {
  const f = operation.formulaire || {};

  const dateDebutConsultation =
    formatDateForApi(f.dateDebutConsultation as string | undefined) ||
    new Date().toISOString().split('T')[0];

  const dateFinConsultation =
    formatDateForApi(f.dateFinConsultation as string | undefined) ||
    addDays(new Date(dateDebutConsultation), 10).toISOString().split('T')[0];

  const dateDebutOperation =
    formatDateForApi(f.dateDebutOperation) ||
    addDays(new Date(dateFinConsultation), 1).toISOString().split('T')[0];

  const delai = (f.delaisFinOperation as number | undefined) || 30;
  const dateFinOperation =
    formatDateForApi(f.dateFinOperation) ||
    addDays(new Date(dateDebutOperation), Number(delai)).toISOString().split('T')[0];

  return {
    nom: f.nomOperation?.trim() || '',
    reference: f.referenceInterne?.trim() || '',
    besoin: f.besoin?.trim() || '',
    adresse: f.adresse?.trim() || '',
    code_postal: f.codePostal?.trim() || '',
    ville: f.ville?.trim() || '',
    facturation: f.coordonneesMaitriseDOuvrage?.trim() || '',
    coordonnees_maitrise_ouvrage: f.coordonneesMaitrise?.trim() || '',
    condition_visite: f.conditionVisite?.trim() || '',
    visite: f.visitePreavis ? '1' : '0',
    date_debut_operation: dateDebutOperation,
    date_fin_operation: dateFinOperation,
    date_debut_consultation: dateDebutConsultation,
    date_fin_consultation: dateFinConsultation,
    delai_execution: delai,
  };
};

// --- Corps de LANCEMENT de consultation (PUT /bdci/operation/{id}/consultation) -
// Port fidele du payload V1 (services/entityService.launchConsultation, branche BC/BCI) :
// consultation a 10 jours en dur, dates calculees si absentes.
// NB (a confirmer) : le swap des coordonnees ici suit le V1 (facturation <- coordonneesMaitrise,
// coordonnees_maitrise <- coordonneesMaitriseDOuvrage), INVERSE de buildConsultationSaveBody (mapping
// BC V2). La sauvegarde precede le lancement, donc ce payload sert surtout a declencher la transition ;
// l'incoherence de swap merite une verification fonctionnelle.
export const buildConsultationLaunchBody = (operation: OperationBCI): Record<string, unknown> => {
  const f = operation.formulaire || {};

  const finConsultation = addDays(new Date(), 10);
  const debutOpDefaut = addDays(finConsultation, 1).toISOString().split('T')[0];
  const dateDebutOperation =
    formatDateForApi(f.dateDebutOperation) ||
    formatDateForApi(operation.date_debut_operation) ||
    debutOpDefaut;

  const delai = Number(f.delaisFinOperation || 30);
  const dateFinOperation =
    formatDateForApi(f.dateFinOperation) ||
    formatDateForApi(operation.date_fin_operation) ||
    addDays(new Date(dateDebutOperation), delai).toISOString().split('T')[0];

  return {
    nom: f.nomOperation?.trim() || operation.nom || '',
    reference: f.referenceInterne?.trim() || operation.reference || '',
    adresse: f.adresse?.trim() || operation.adresse || '',
    code_postal: f.codePostal?.trim() || operation.code_postal || '',
    ville: f.ville?.trim() || operation.ville || '',
    facturation: f.coordonneesMaitrise?.trim() || operation.coordonnees_maitrise || '',
    coordonnees_maitrise: f.coordonneesMaitriseDOuvrage?.trim() || operation.coordonnees_maitrise_ouvrage || '',
    consultation: '10',
    besoin: f.besoin?.trim() || operation.besoin || '',
    visite: f.visitePreavis ? '1' : '0',
    condition_visite: f.conditionVisite?.trim() || operation.condition_visite || '',
    date_debut_operation: dateDebutOperation,
    date_fin_operation: dateFinOperation,
  };
};

// --- formData enrichi pour OperationFormulaireAdmin (editable) ------------------
export const buildConsultationFormData = (operation: OperationBCI): OperationFormulaire => {
  const base = operation.formulaire || {};
  if (base.delaisFinOperation) {
    return { ...base, delaisConsultation: 10 };
  }

  let delaisFinOperation: number = 30;
  if (operation.delai_execution) {
    delaisFinOperation = Number(operation.delai_execution);
  } else if (operation.date_debut_operation && operation.date_fin_operation) {
    const debut = new Date(operation.date_debut_operation);
    const fin = new Date(operation.date_fin_operation);
    const diff = Math.abs(fin.getTime() - debut.getTime());
    delaisFinOperation = Math.ceil(diff / (1000 * 60 * 60 * 24));
  }

  return { ...base, delaisConsultation: 10, delaisFinOperation };
};
